import type React from "react";
import { Box, Center, Loader, Progress, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import type {
  JobViewModel,
  RowEdit,
} from "@models/job";
import { isOeRow, isTmRow } from "@models/mappingResult";
import type { MappingResultRow } from "@models/mappingResult";
import type {
  RowSaveStatus,
  UseJobResultsState,
} from "@hooks/useJobResults";
import WdJobResultsTable from "./table/wd-mapping/WdJobResultsTable";
import TmJobResultsTable from "./table/technical-manual/TmJobResultsTable";
import ResultsFilterChips from "./table/ResultsFilterChips";
import {
  ResultTableVariant,
  resolveResultTableVariant,
} from "@utils/resultTableVariant";
import type { ReviewStatusFilter } from "@utils/jobResultsHelpers";

interface JobResultsBodyProps {
  job: JobViewModel | null;
  jobId: string;
  showResults: boolean;
  results: UseJobResultsState;
  filteredRows: MappingResultRow[];
  statusFilter: ReviewStatusFilter;
  onStatusFilterChange: (value: ReviewStatusFilter) => void;
  statusCounts: Record<ReviewStatusFilter, number>;
  priorityOnly: boolean;
  onPriorityOnlyChange: (value: boolean) => void;
  priorityCount: number;
  missingOnly: boolean;
  onMissingOnlyChange: (value: boolean) => void;
  missingCount: number;
  edits: Map<number, RowEdit>;
  saveStates: Map<number, RowSaveStatus>;
  /** Keyboard-focused row: highlighted by the default (generic) table only. */
  registerRowRef: (rowIndex: number, el: HTMLTableRowElement | null) => void;
  onAccept: (rowIndex: number, text?: string) => void;
  onReject: (rowIndex: number) => void;
  onPickAlternative: (rowIndex: number, rank: number) => void;
  onRecover: (rowIndex: number) => void;
  onRetrySave: (rowIndex: number) => void;
  onRowRestored: (row: MappingResultRow) => void;
  onLoadMore: () => void;
  onAddRow: (
    itemMpId: number,
    itemMpText: string,
    value: string,
  ) => Promise<void>;
}

const JobResultsBody: React.FC<JobResultsBodyProps> = ({
  job,
  jobId,
  showResults,
  results,
  filteredRows,
  statusFilter,
  onStatusFilterChange,
  statusCounts,
  priorityOnly,
  onPriorityOnlyChange,
  priorityCount,
  missingOnly,
  onMissingOnlyChange,
  missingCount,
  edits,
  saveStates,
  registerRowRef,
  onAccept,
  onReject,
  onPickAlternative,
  onRecover,
  onRetrySave,
  onRowRestored,
  onLoadMore,
  onAddRow,
}) => {
  const { t } = useTranslation();

  const renderResultTable = (): React.ReactNode => {
    switch (resolveResultTableVariant(job?.pluginId)) {
      case ResultTableVariant.TechnicalManual:
        return (
          <TmJobResultsTable
            rows={filteredRows.filter(isTmRow)}
            edits={edits}
            saveStates={saveStates}
            jobId={jobId}
            registerRowRef={registerRowRef}
            onAccept={onAccept}
            onReject={onReject}
            onPickAlternative={onPickAlternative}
            onRecover={onRecover}
            onRetrySave={onRetrySave}
            onRowRestored={onRowRestored}
            hasMore={results.hasMore}
            loadingMore={results.loadingMore}
            onLoadMore={onLoadMore}
            onLoadAlternatives={results.loadAlternatives}
            alternativesLoading={results.alternativesLoading}
            alternatives={results.alternatives}
            onAddRow={onAddRow}
          />
        );
      case ResultTableVariant.Wd:
        return (
          <WdJobResultsTable
            rows={filteredRows.filter(isOeRow)}
            edits={edits}
            saveStates={saveStates}
            jobId={jobId}
            registerRowRef={registerRowRef}
            onAccept={onAccept}
            onReject={onReject}
            onPickAlternative={onPickAlternative}
            onRecover={onRecover}
            onRetrySave={onRetrySave}
            onRowRestored={onRowRestored}
            hasMore={results.hasMore}
            loadingMore={results.loadingMore}
            onLoadMore={onLoadMore}
            onLoadAlternatives={results.loadAlternatives}
            alternativesLoading={results.alternativesLoading}
            alternatives={results.alternatives}
          />
        );
      // A plugin the map does not know still returns rows tagged `tm` — the
      // backend's base service builds the TM shape for an unregistered
      // service, so that is what the fallback has to render. There is no
      // generic row shape on the wire any more to render generically.
      default:
        return (
          <TmJobResultsTable
            rows={filteredRows.filter(isTmRow)}
            edits={edits}
            saveStates={saveStates}
            jobId={jobId}
            registerRowRef={registerRowRef}
            onAccept={onAccept}
            onReject={onReject}
            onPickAlternative={onPickAlternative}
            onRecover={onRecover}
            onRetrySave={onRetrySave}
            onRowRestored={onRowRestored}
            hasMore={results.hasMore}
            loadingMore={results.loadingMore}
            onLoadMore={onLoadMore}
            onLoadAlternatives={results.loadAlternatives}
            alternativesLoading={results.alternativesLoading}
            alternatives={results.alternatives}
            onAddRow={onAddRow}
          />
        );
    }
  };

  return (
    <Box className="job-results-page__body">
      {job?.error && (
        <Box className="job-results-page__job-meta">
          <Text size="sm" c="red">
            {job.error.message}
          </Text>
        </Box>
      )}

      {showResults && results.rows.length > 0 && (
        <ResultsFilterChips
          value={statusFilter}
          onChange={onStatusFilterChange}
          counts={statusCounts}
          priorityOnly={priorityOnly}
          onPriorityOnlyChange={onPriorityOnlyChange}
          priorityCount={priorityCount}
          missingOnly={missingOnly}
          onMissingOnlyChange={onMissingOnlyChange}
          missingCount={missingCount}
        />
      )}

      {!showResults && job && (
        <Center mih={200}>
          <Stack align="center" gap="xs">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              {t("mappingServicePage.main.waitingForRows")}
            </Text>
          </Stack>
        </Center>
      )}

      {showResults && results.loading && (
        <Center mih={200}>
          <Stack align="center" gap="xs">
            <Loader size="sm" />
            <Text size="sm">
              {t("mappingServicePage.main.loadingResults")}
            </Text>
          </Stack>
        </Center>
      )}

      {showResults && results.notReady && (
        <Center mih={200}>
          <Stack align="center" gap="xs" w={320} maw="80%">
            <Text size="sm">
              {t("mappingServicePage.main.processingRows")}
            </Text>
            {job && job.rowsTotal ? (
              <>
                <Progress
                  w="100%"
                  value={Math.min(
                    100,
                    Math.round(
                      ((job.rowsDone ?? 0) / job.rowsTotal) * 100,
                    ),
                  )}
                  transitionDuration={400}
                />
                <Text size="xs" c="dimmed">
                  {t("mappingServicePage.main.rowsCount", {
                    done: job.rowsDone ?? 0,
                    total: job.rowsTotal,
                  })}
                </Text>
              </>
            ) : (
              <>
                <Loader size="sm" />
                <Text size="xs" c="dimmed">
                  {t("mappingServicePage.main.resultsNotReady")}
                </Text>
              </>
            )}
          </Stack>
        </Center>
      )}

      {showResults &&
        !results.loading &&
        !results.error &&
        results.rows.length === 0 &&
        !results.notReady && (
          <Center mih={200}>
            <Text size="sm" c="dimmed">
              {t("mappingServicePage.main.noRows")}
            </Text>
          </Center>
        )}

      {showResults &&
        results.rows.length > 0 &&
        filteredRows.length === 0 && (
          <Center mih={120}>
            <Text size="sm" c="dimmed">
              {t("jobResultsPage.filters.noMatches")}
            </Text>
          </Center>
        )}

      {showResults && filteredRows.length > 0 && renderResultTable()}
    </Box>
  );
};

export default JobResultsBody;
